import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

type UserRole = 'STUDENT' | 'TUTOR' | 'ADMIN' | 'PARENT';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
}

/**
 * RoleGuard component that restricts access based on user role.
 * Redirects to /dashboard (or custom path) if user doesn't have required role.
 * Should be used inside ProtectedRoute to ensure user is authenticated first.
 */
export function RoleGuard({ children, allowedRoles, redirectTo = '/dashboard' }: RoleGuardProps) {
  const { user } = useAuth();

  // If no user (shouldn't happen if inside ProtectedRoute), redirect
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user's role is in the allowed roles
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  // User has required role, render content
  return <>{children}</>;
}
